import React from 'react';

import AuthContext from './contexts';
import instance from '@connection/instance';
import { verifyJWT } from '@utils/jwt';

import {
  writeToLocalStorage,
  clearLocalStorage,
  unsetDefaultHeader,
  setDefaultHeader
} from './utils';

import {
  SIGNED_IN_MODE,
  SET_USER
} from './constants';

function useAuth() {
  const [state, dispatch] = React.useContext(AuthContext);

  const isThisAuthContext = (
    Object.prototype.hasOwnProperty.call(state, 'user')
  );

  // ToDo. This check dont complete. Error don't throw when hook use outside AuthProvider
  if (!isThisAuthContext) {
    throw new Error('useAuth must be used within a AuthProvider');
  }

  const isAuthenticated = state.mode === SIGNED_IN_MODE;

  const setUser = (userData) => dispatch({ type: SET_USER, data: userData });

  const setDataUser = (userData) => {
    setUser(userData);
    writeToLocalStorage({ ...userData });
  }

  const loginByToken = (params = {}) => {
    return verifyJWT(params)
      .then(data => data.data.response)
      .then(data => setDefaultHeader(data))
      .then(data => writeToLocalStorage(data))
      .then(data => setUser(data))
      .catch(err => console.error(err));
  };

  const verify = (data, onSuccess, onError) => {
    return instance.post(`/account/verify?vt=${data}`)
    .then(data => data.data)
    .then(data => {
      if (data.errors) {
        onError && onError(data.errors);
        
        return data.errors;
      }

      setDefaultHeader(data.response);
      setUser(data.response);
      writeToLocalStorage({ ...data.response, email: data.email });
      onSuccess && onSuccess(data.response);

      return data;
    })
      .catch(err => console.error(err));
  };

  const checkCapcha = (data, onSuccess, onError) => {
    return instance.get(`/recaptcha?reToken=${data}`)
    .then(data => data.data)
    .then(data => {
      if (data.errors) {
        return data.errors;
      }

      onSuccess && onSuccess(data.response);

      return data;
    })
      .catch(err => {
        onError && onSuccess();
        console.log(err);
        return { error: 'error' }
      });
  };

  const register = (data, callback) => {
    return instance.post('/account/signup', data)
      .then(data => data.data)
      .then(data => {
        if (data.errors) {
          return data.errors;
        }

        callback && callback();
      })
      .catch(err => console.error(err));
  };

  const sendAgain = ({ email }, callback) => {
    return instance.get(`/account/verify?email=${email}`)
      .then(data => data.data)
      .then(data => {
        if (data.errors) {
          return data.errors;
        }

        callback && callback();
        
        window.gtag('event', 'action_registration_complete')
      })
      .catch(err => console.error(err));
  };

  const login = (data, callback, onInactive) => {
    return instance.post('/account/signin', data)
      .then(data => data.data)
      .then(data => {
        if (data.errors) {
          return data.errors;
        }

        if (!data.response.active) {
          onInactive && onInactive(data.response);
          return;
        }

        setDefaultHeader(data.response);
        setUser(data.response);
        writeToLocalStorage({ ...data.response, email: data.email });
        callback && callback(data.response);

        return data;
      });
  };

  const logout = () => {
    return instance.post('/api/logout')
      .then(data => data.data)
      .then(data => {
        if (data.errors) {
          return data.errors;
        }

        clearLocalStorage();
        unsetDefaultHeader();
        setUser(null);

        return data;
      }).
      catch(err => {
        clearLocalStorage();
        unsetDefaultHeader();
        setUser(null);
      })
  };

  const forget = (data, callback) => {
    return instance.post(`/account/forgetPassword?email=${data.email}`)
      .then(data => data.data)
      .then(data => {
        if (data.errors) {
          return data.errors;
        }

        callback && callback(data.response);

        return data;
      })
      .catch(err => console.error(err));
  };

  const updatePassword = (token, data, callback) => {
    return instance.post(`/account/updatePassword?vt=${token}`, data)
      .then(data => data.data)
      .then(data => {
        if (data.errors) {
          return data.errors;
        }

        callback && callback();

        return data;
      })
      .catch(err => console.error(err));
  };

  const resetPassword = (data) => {
    return instance.post('/api/auth/password', { user: data })
      .catch(err => console.error(err));
  };

  const checkEmailExistence = (email) => {
    return instance.get('/api/auth/check_email', { params: { email } });
  };

  const selectors = {
    currentUser: state.user,
    isAuthorized: state.isAuthorized,
    filled: state.filled,
    isAuthenticated
  };

  const actions = {
    checkEmailExistence,
    loginByToken,
    register,
    forget,
    sendAgain,
    login,
    checkCapcha,
    updatePassword,
    setDataUser,
    logout,
    resetPassword,
    verify
  };

  return [
    selectors,
    actions
  ];
}

export default useAuth;
